import { useState, useRef, useEffect } from 'react'

interface InlinePriceInputProps {
  value: string | null
  onSave: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  prefix?: string
}

function formatPrice(value: string | null): string {
  if (value == null || value === '') return ''
  const num = parseFloat(value)
  if (isNaN(num)) return ''
  return num.toFixed(2).replace('.', ',')
}

function parsePrice(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === '') return null
  const normalized = trimmed.replace(',', '.')
  const num = parseFloat(normalized)
  if (isNaN(num) || num < 0) return null
  return num.toFixed(2)
}

export default function InlinePriceInput({ value, onSave, placeholder = '0,00', disabled, prefix }: InlinePriceInputProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  function startEditing() {
    if (disabled) return
    setInputValue(formatPrice(value))
    setIsEditing(true)
  }

  function handleBlur() {
    const parsed = parsePrice(inputValue)
    const currentFormatted = value != null ? parseFloat(value).toFixed(2) : null
    if (parsed !== currentFormatted) {
      onSave(parsed)
    }
    setIsEditing(false)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      inputRef.current?.blur()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className="input input-sm input-bordered w-24 text-right"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      disabled={disabled}
      className="text-right rounded hover:bg-base-300 transition-colors cursor-pointer disabled:cursor-default disabled:opacity-50 inline-block"
    >
      {value != null
        ? <>{prefix && <span>{prefix} </span>}{formatPrice(value)} €</>
        : <span className="opacity-40">{placeholder}</span>
      }
    </button>
  )
}
